<script setup>
import { computed, ref, onMounted } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import PlusIcon from '@/icons/plus.vue';
import MinusIcon from '@/icons/minus.vue';
import { useLayoutComponents } from '@/composables/layout';
import { useStorage } from '@/composables/use-storage';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import Multiselect from '@/components/forms/inputs/multiselect.vue';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import { fetchAllBadges } from '@/pages/admin/user-badges/services/badgesService.js';

const { TextInput } = useLayoutComponents();
const { hasPermission } = useStorage();
const { addToast } = useToast();

const users = ref([]);
const userGroups = ref([]);
const badges = ref([]);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];
const expandedRows = ref([]);
const editingBadgesForUserId = ref(null);
const editingBadges = ref([]);
const isLoading = ref(false);

const fetchUsers = async () => {
    try {
        if (hasPermission('group-set-users')) {
            const response = await axios.get('/api/user-groups');
            users.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching users:', error);
        addToast('Failed to fetch users', 'error');
    }
};

const fetchGroups = async () => {
    try {
        if (hasPermission('group-manager') || hasPermission('group-set-users')) {
            const response = await axios.get('/api/groups');
            userGroups.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching groups:', error);
        addToast('Failed to fetch groups', 'error');
    }
};

const fetchBadges = async () => {
    try {
        const response = await fetchAllBadges();
        if (response) {
            badges.value = response;
        }
    } catch (error) {
        console.error('Error fetching badges:', error);
        addToast('Failed to fetch badges', 'error');
    }
};

onMounted(() => {
    fetchUsers();
    fetchGroups();
    fetchBadges();
});

const showChangeCreditsModal = ref(false);
const userSelected = ref(null);
const creditsAmount = ref('0.00');
const creditsAction = ref('add');

const filteredUsers = computed(() => {
    let filtered = users.value;
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = users.value.filter(user => 
            user.username.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query)
        );
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredUsers.value.length / itemsPerPage.value);
});

const paginatedUsers = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredUsers.value.slice(start, end);
});

const updateUserGroup = async (user) => {
    try {
        await axios.post(`/api/user-groups`, {
            user_id: user.id,
            group_id: user.user_group
        });
        addToast('User group updated successfully', 'success');
    } catch (error) {
        console.error('Error updating user group:', error);
        addToast('Failed to update user group', 'error');
        user.user_group = null;
    }
};

const badgesOptions = computed(() => {
    if (!badges.value || badges.value.length === 0) {
        return [];
    }
    
    return badges.value
        .filter(badge => {
            if (!badge || !badge.id) return false;
            return badge.name;
        })
        .map(badge => {
            const name = badge.name ? badge.name.trim() : '';
            const description = badge.description ? badge.description.trim() : '';
            const label = description ? `${name} - ${description}` : name;
            
            return {
                value: Number(badge.id),
                label: String(label).trim()
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
});


const haddleShowAddCreditsModal = (user, action) => {
    userSelected.value = user;
    showChangeCreditsModal.value = true;
    creditsAmount.value = '0.00';
    creditsAction.value = action;
}

const newCreditsAmount = computed(() => {
    const userCredits = parseFloat(userSelected.value?.credits || 0);
    const changeCredits = parseFloat(creditsAmount.value?.replace(/[,\s]/g, '') || 0);
    
    if (creditsAction.value === 'add') {
        return (userCredits + changeCredits).toFixed(2);
    } else {
        const result = userCredits - changeCredits;
        return Math.max(0, result).toFixed(2);
    }
});

const maxRemovableCredits = computed(() => {
    if (creditsAction.value === 'remove' && userSelected.value) {
        return parseFloat(userSelected.value.credits || 0);
    }
    return null;
});

const isOverLimit = computed(() => {
    if (creditsAction.value === 'remove' && userSelected.value) {
        const removeAmount = parseFloat(creditsAmount.value?.replace(/[,\s]/g, '') || 0);
        const userCredits = parseFloat(userSelected.value.credits || 0);
        return removeAmount > userCredits;
    }
    return false;
});

const formattedNewCreditsAmount = computed(() => {
    const value = newCreditsAmount.value;
    const [integerPart, decimalPart] = value.split('.');
    const formattedInteger = formatNumberWithThousandSeparator(integerPart);
    return `${formattedInteger}.${decimalPart}`;
});

const formattedUserCredits = computed(() => {
    const value = parseFloat(userSelected.value?.credits || 0).toFixed(2);
    const [integerPart, decimalPart] = value.split('.');
    const formattedInteger = formatNumberWithThousandSeparator(integerPart);
    return `${formattedInteger}.${decimalPart}`;
});

const formatCreditsInput = (event) => {
    let value = event.target.value;
    
    value = value.replace(/\D/g, '');
    
    if (value === '') {
        creditsAmount.value = '';
        return;
    }
    if (value.length > 10) {
        value = value.substring(0, 10);
    }
    
    const numericValue = parseInt(value);
    let formattedValue = (numericValue / 100).toFixed(2);
    
    if (creditsAction.value === 'remove' && userSelected.value) {
        const maxCredits = parseFloat(userSelected.value.credits || 0);
        const inputValue = parseFloat(formattedValue);
        if (inputValue > maxCredits) {
            formattedValue = maxCredits.toFixed(2);
        }
    }
    
    const [integerPart, decimalPart] = formattedValue.split('.');
    const formattedInteger = formatNumberWithThousandSeparator(integerPart);
    
    creditsAmount.value = `${formattedInteger}.${decimalPart}`;
};

const handleKeyDown = (event) => {
    if (event.key === 'ArrowUp') {
        event.preventDefault();
        const currentValue = parseFloat(creditsAmount.value.replace(/[,\s]/g, '') || 0);
        const newValue = (currentValue + 1).toFixed(2);
        const [integerPart, decimalPart] = newValue.split('.');
        const formattedInteger = formatNumberWithThousandSeparator(integerPart);
        creditsAmount.value = `${formattedInteger}.${decimalPart}`;
    }
    
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        const currentValue = parseFloat(creditsAmount.value.replace(/[,\s]/g, '') || 0);
        const newValue = Math.max(0, currentValue - 1).toFixed(2);
        const [integerPart, decimalPart] = newValue.split('.');
        const formattedInteger = formatNumberWithThousandSeparator(integerPart);
        creditsAmount.value = `${formattedInteger}.${decimalPart}`;
    }
    
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];
    if (!allowedKeys.includes(event.key) && !/\d/.test(event.key)) {
        event.preventDefault();
    }
    
    if (event.key === 'Backspace' && creditsAmount.value === '') {
        setTimeout(() => {
            creditsAmount.value = '0.00';
        }, 0);
    }
};

const formatOnBlur = () => {
    if (!creditsAmount.value || creditsAmount.value === '') {
        creditsAmount.value = '0.00';
    } else {
        const numericValue = parseFloat(creditsAmount.value.replace(/[,\s]/g, '')).toFixed(2);
        const [integerPart, decimalPart] = numericValue.split('.');
        const formattedInteger = formatNumberWithThousandSeparator(integerPart);
        creditsAmount.value = `${formattedInteger}.${decimalPart}`;
    }
};

const addCredits = async () => {
    try {
        const addAmount = parseFloat(creditsAmount.value.replace(/[,\s]/g, ''));
        if (addAmount <= 0) {
            addToast('Amount must be greater than zero', 'error');
            return;
        }
        
        await axios.post(`/api/users/add-credits`, {
            user_id: userSelected.value.id,
            amount: addAmount
        });

        userSelected.value.credits = newCreditsAmount.value;
        
        addToast('Credits added successfully', 'success');
        showChangeCreditsModal.value = false;
    } catch (error) {
        console.error('Error adding credits:', error);
        addToast('Failed to add credits', 'error');
    }
};

const removeCredits = async () => {
    try {
        const removeAmount = parseFloat(creditsAmount.value.replace(/[,\s]/g, ''));
        const userCredits = parseFloat(userSelected.value.credits || 0);
        
        if (removeAmount > userCredits) {
            addToast('Cannot remove more credits than available', 'error');
            return;
        }
        
        if (removeAmount <= 0) {
            addToast('Amount must be greater than zero', 'error');
            return;
        }
        
        await axios.post(`/api/users/remove-credits`, {
            user_id: userSelected.value.id,
            amount: removeAmount
        });

        userSelected.value.credits = newCreditsAmount.value;
        
        addToast('Credits removed successfully', 'success');
        showChangeCreditsModal.value = false;
    } catch (error) {
        console.error('Error removing credits:', error);
        addToast('Failed to remove credits', 'error');
    }
};

const formatNumberWithThousandSeparator = (number) => {
   
    return Number(number).toLocaleString('en-US');
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const formatTime = (time) => {
    return new Date(time).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const toggleDetails = (id) => {
    const index = expandedRows.value.indexOf(id);
    if (index === -1) {
        expandedRows.value.push(id);
    } else {
        expandedRows.value.splice(index, 1);
    }
};

const getBadgeInitials = (badge) => {
    if (!badge.name) return '??';
    
    const name = badge.name.trim();
    
    if (name.includes(' ')) {
        return name
            .split(' ')
            .filter(word => word.length > 0)
            .map(word => word.charAt(0).toUpperCase())
            .join('')
            .slice(0, 2);
    }
    
    return name.slice(0, 2).toUpperCase();
};

const startEditingBadges = (user) => {
    editingBadgesForUserId.value = user.id;
    editingBadges.value = user.badges ? [...user.badges] : [];
};

const cancelEditingBadges = (user) => {
    if (isLoading.value) return;
    editingBadgesForUserId.value = null;
    editingBadges.value = [];
};

const removeBadge = (badgeItemId) => {
    if (isLoading.value) return;
    editingBadges.value = editingBadges.value.filter(b => b.id !== badgeItemId);
};

const getSelectedBadgeIds = (user) => {
    if (editingBadgesForUserId.value === user.id) {
        return editingBadges.value.map(b => {
            const id = b.badge_id || b.badge?.id;
            return id ? Number(id) : null;
        }).filter(id => id !== null);
    }
    return user.badges ? user.badges.map(b => {
        const id = b.badge_id || b.badge?.id;
        return id ? Number(id) : null;
    }).filter(id => id !== null) : [];
};

const updateSelectedBadges = (user, selectedBadgeIds) => {
    if (editingBadgesForUserId.value !== user.id) return;
    if (isLoading.value) return;
    
    const selectedIds = selectedBadgeIds.map(id => Number(id));
    
    editingBadges.value = editingBadges.value.filter(b => {
        const badgeId = b.badge_id || b.badge?.id;
        return badgeId ? selectedIds.includes(Number(badgeId)) : false;
    });
    
    const currentBadgeIds = editingBadges.value.map(b => {
        const id = b.badge_id || b.badge?.id;
        return id ? Number(id) : null;
    }).filter(id => id !== null);
    const newBadgeIds = selectedIds.filter(id => !currentBadgeIds.includes(id));
    
    newBadgeIds.forEach(badgeId => {
        const badge = badges.value.find(b => Number(b.id) === badgeId);
        if (badge) {
            editingBadges.value.push({
                id: `temp-${Date.now()}-${badgeId}`,
                user_id: user.id,
                badge_id: badgeId,
                badge: badge
            });
        }
    });
};

const saveUserBadges = async (user) => {
    try {
        isLoading.value = true;
        const currentBadgeIds = user.badges ? user.badges.map(b => {
            const id = b.badge_id || b.badge?.id;
            return id ? Number(id) : null;
        }).filter(id => id !== null) : [];
        const newBadgeIds = editingBadges.value.map(b => {
            const id = b.badge_id || b.badge?.id;
            return id ? Number(id) : null;
        }).filter(id => id !== null);
        
        const badgesToAdd = newBadgeIds.filter(id => !currentBadgeIds.includes(id));
        const badgesToRemove = currentBadgeIds.filter(id => !newBadgeIds.includes(id));
        
        for (const badgeId of badgesToRemove) {
            const badgeItem = user.badges.find(b => {
                const id = b.badge_id || b.badge?.id;
                return id ? Number(id) === badgeId : false;
            });
            if (badgeItem && badgeItem.id) {
                await axios.delete(`/api/user-badges/${badgeItem.id}`);
            }
        }
        
        for (const badgeId of badgesToAdd) {
            await axios.post(`/api/user-badges`, {
                user_id: Number(user.id),
                badge_id: Number(badgeId)
            });
        }
        
        await fetchUsers();
        
        addToast('Badges updated successfully', 'success');
        cancelEditingBadges(user);
    } catch (error) {
        console.error('Error updating user badges:', error);
        addToast('Failed to update badges', 'error');
    } finally {
        isLoading.value = false;
    }
};

</script>
<template>
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <UsersHeader>
            <template #title>Users Management</template>
            <template #description>
                Manage user accounts, permissions, and credits from a central dashboard.
            </template>
        </UsersHeader>

        <!-- Stats Cards -->
        <div class="mt-6 grid grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="uil uil-users-alt text-2xl text-sky-500"></i>
                </template>
                <template #title>Total Users</template>
                <template #value>{{ users.length }}</template>
            </StatCard>

            <StatCard>
                <template #icon>
                    <i class="uil uil-user text-2xl text-purple-500"></i>
                </template>
                <template #title>User Groups</template>
                <template #value>{{ userGroups.length }}</template>
            </StatCard>

            <StatCard>
                <template #icon>
                    <i class="uil uil-usd-circle text-2xl text-amber-500"></i>
                </template>
                <template #title>Total Distributed Credits</template>
                <template #value>
                    {{ users.reduce((sum, user) => sum + parseFloat(user.credits || 0), 0).toFixed(2) }}
                </template>
            </StatCard>
        </div>

        <!-- Main Content -->
        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by username or email" 
                                class="w-full"
                            />
                        </div>
                        <div class="w-full sm:w-24">
                            <SelectInput
                                v-model="itemsPerPage"
                                :options="itemsPerPageOptions"
                                :hide-search="true"
                                @update:modelValue="currentPage = 1"
                            />
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                    <table class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light ">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    User Details
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Signup
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Group
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Credits
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="user in paginatedUsers" :key="user.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-primary flex items-center justify-center">
                                                    <span class="text-lg font-medium text-white">
                                                        <img v-if="user.profile_pic" :src="user.profile_pic" alt="user-avatar" class="h-10 w-10 rounded-full">
                                                        <span v-else class="text-lg font-medium text-white">
                                                            {{ user.username.charAt(0).toUpperCase() }}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white">
                                                    {{ user.username }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ user.email }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex flex-col justify-center items-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(user.signup) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(user.signup) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <SelectInput
                                            :id="`user-group-${user.id}`"
                                            v-model="user.user_group"
                                            :disabled="user.id === '1'"
                                            :options="[
                                                { value: null, label: 'No Group' },
                                                ...userGroups.map(group => ({
                                                    value: group.id,
                                                    label: group.name
                                                }))
                                            ]"
                                            @update:modelValue="updateUserGroup(user)"
                                        />
                                    </td>
                              
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center space-x-4">
                                            <button 
                                                @click="haddleShowAddCreditsModal(user, 'remove')"
                                                class="text-red-500 hover:text-red-400 transition-colors duration-200"
                                            >
                                                <MinusIcon class="h-5 w-5 hover:text-white/80" />
                                            </button>
                                            <span class="text-sm font-medium text-white">
                                                {{ user.credits }}
                                            </span>
                                            <button 
                                                @click="haddleShowAddCreditsModal(user, 'add')"
                                                class="text-green-500 hover:text-green-400 transition-colors duration-200"
                                            >
                                                <PlusIcon class="h-5 w-5 hover:text-white/80" />
                                            </button>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex justify-center">
                                            <button 
                                                @click="toggleDetails(user.id)"
                                                class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                                            >
                                                <svg 
                                                    class="w-5 h-5 mr-2 text-white/80 transition-transform duration-200"
                                                    :class="{ 'transform rotate-180': expandedRows.includes(user.id) }"
                                                    xmlns="http://www.w3.org/2000/svg" 
                                                    viewBox="0 0 20 20" 
                                                    fill="currentColor"
                                                >
                                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                </svg>
                                                {{ expandedRows.includes(user.id) ? 'Hide Badges' : 'Show Badges' }}
                                            </button>
                                        </div>
                                    </td>   
                                </tr>
                                <!-- Expanded Row with Details -->
                                <tr v-if="expandedRows.includes(user.id)" class="bg-echoes-background-black border-b border-echoes-border-light">
                                    <td colspan="6" class="px-6 py-6">
                                        <div class="grid grid-cols-1">

                                            <!-- Badges Section -->
                                            <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-6">
                                                <h4 class="text-sm font-semibold text-white mb-4 flex items-center justify-between border-b border-echoes-border-light pb-3">
                                                    <div class="flex items-center">
                                                        <i class="uil uil-award text-echoes-primary mr-2 text-xl"></i>
                                                        User Badges
                                                        <span v-if="(editingBadgesForUserId === user.id ? editingBadges : user.badges) && (editingBadgesForUserId === user.id ? editingBadges : user.badges).length > 0" class="ml-2 text-xs text-white/60">({{ editingBadgesForUserId === user.id ? editingBadges.length : user.badges.length }})</span>
                                                    </div>
                                                    <button
                                                        v-if="editingBadgesForUserId !== user.id"
                                                        @click="startEditingBadges(user)"
                                                        class="text-xs px-3 py-1.5 rounded-lg bg-echoes-primary hover:bg-echoes-primary/90 text-white transition-colors duration-200 flex items-center gap-1"
                                                    >
                                                        <i class="uil uil-pen text-sm"></i>
                                                        Edit
                                                    </button>
                                                    <div v-else class="flex items-center gap-2">
                                                        <button
                                                            @click="cancelEditingBadges(user)"
                                                            :disabled="isLoading"
                                                            class="text-sm px-3 py-1.5 rounded-lg bg-echoes-background-black hover:bg-echoes-background-black/80 text-white/80 hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            @click="saveUserBadges(user)"
                                                            :disabled="isLoading"
                                                            class="text-sm px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <div v-if="isLoading" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                            <span>{{ isLoading ? 'Saving...' : 'Save' }}</span>
                                                        </button>
                                                    </div>
                                                </h4>
                                            
                                                <!-- Edit Mode: Multiselect for adding badges -->
                                                <div v-if="editingBadgesForUserId === user.id" class="mb-4 w-1/2">
                                                    <label :for="`badge-select-${user.id}`" class="block text-sm font-medium text-white/80 mb-2">Add Badges</label>
                                                    <Multiselect
                                                        :id="`badge-select-${user.id}`"
                                                        :model-value="getSelectedBadgeIds(user)"
                                                        :options="badgesOptions"
                                                        :disabled="isLoading"
                                                        @update:modelValue="(value) => updateSelectedBadges(user, value)"
                                                        placeholder="Select badges to add"
                                                        class="w-full"
                                                    />
                                                </div>
                                            
                                                <!-- Badges Display -->
                                                <div v-if="(editingBadgesForUserId === user.id ? editingBadges : user.badges) && (editingBadgesForUserId === user.id ? editingBadges : user.badges).length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    <div 
                                                        v-for="(badgeItem, index) in (editingBadgesForUserId === user.id ? editingBadges : user.badges)" 
                                                        :key="badgeItem.id"
                                                        class="flex items-center gap-4 bg-echoes-background-black/50 rounded-xl p-4 border border-echoes-border-light hover:bg-echoes-background-black/70 transition-colors duration-200"
                                                        :class="{ 'relative': editingBadgesForUserId === user.id }"
                                                    >
                                                        <!-- Remove Button (only in edit mode) -->
                                                        <button
                                                            v-if="editingBadgesForUserId === user.id"
                                                            @click="removeBadge(badgeItem.id)"
                                                            :disabled="isLoading"
                                                            class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-colors duration-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Remove badge"
                                                        >
                                                            <i class="uil uil-times text-xs"></i>
                                                        </button>
                                                        <!-- Badge Icon/Avatar -->
                                                        <div 
                                                            class="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
                                                            :class="badgeItem.badge?.badge_image ? '' : (!badgeItem.badge?.badge_color ? 'bg-echoes-background-gray' : '')"
                                                            :style="!badgeItem.badge?.badge_image && badgeItem.badge?.badge_color ? { backgroundColor: badgeItem.badge.badge_color } : {}"
                                                        >
                                                            <img 
                                                                v-if="badgeItem.badge?.badge_image" 
                                                                :src="badgeItem.badge.badge_image" 
                                                                alt="Badge" 
                                                                class="w-full h-full object-cover rounded-full"
                                                            >
                                                            <span v-else class="text-white font-bold text-lg">
                                                                {{ getBadgeInitials(badgeItem.badge || {}) }}
                                                            </span>
                                                        </div>
                                                        <!-- Badge Info -->
                                                        <div class="flex-1 min-w-0">
                                                            <p class="text-sm font-bold text-white truncate">
                                                                {{ badgeItem.badge?.name || 'Unknown Badge' }}
                                                            </p>
                                                            <p class="text-xs text-white/60 mt-1 line-clamp-2">
                                                                {{ badgeItem.badge?.description || badgeItem.badge?.category || '' }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <!-- No Badges Message -->
                                                <div v-else class="flex flex-col items-center justify-center py-8 text-white/60">
                                                    <i class="uil uil-award text-5xl mb-3 opacity-50"></i>
                                                    <p class="text-sm">This user has no badges assigned yet</p>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredUsers.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredUsers.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Credits Management Modal -->
        <div v-if="showChangeCreditsModal" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="showChangeCreditsModal = false"></div>
                <div class="relative bg-echoes-background-gray border border-echoes-border-light rounded-xl max-w-md w-full">
                    <!-- Modal Header -->
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                                {{ creditsAction === 'add' ? 'Add Credits' : 'Remove Credits' }}
                            </h2>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {{ userSelected.username }}
                            </p>
                        </div>
                        <button 
                            @click="showChangeCreditsModal = false" 
                            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                        >
                            <span class="sr-only">Close</span>
                            <svg class="p-2 w-10 h-10 rounded-xl text-white/80 hover:text-red-500/80 bg-transparent hover:bg-white/5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Modal Content -->
                    <div class="p-6 space-y-6">
                        <!-- Current Credits -->
                        <div class="bg-echoes-background-gray rounded-xl">
                            <div class="text-sm text-gray-500 dark:text-gray-400">Current Balance</div>
                            <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                                {{ formattedUserCredits }} credits
                            </div>
                        </div>

                        <!-- Credits Input -->
                        <div>
                            <label for="credits-amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {{ creditsAction === 'add' ? 'Amount to Add' : 'Amount to Remove' }}
                            </label>
                            <TextInput
                                v-model="creditsAmount"
                                id="credits-amount"
                                type="text"
                                :placeholder="creditsAction === 'add' ? 'Enter amount to add' : 'Enter amount to remove'"
                                pattern="^\d+(\.\d{1,2})?$"
                                @input="formatCreditsInput"
                                @keydown="handleKeyDown"
                                @blur="formatOnBlur"
                                :class="{ 'border-red-500 ring-red-500': isOverLimit }"
                                class="block w-full"
                            />
                            <div v-if="creditsAction === 'remove' && maxRemovableCredits !== null" 
                                 class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Maximum removable: {{ formatNumberWithThousandSeparator(maxRemovableCredits.toFixed(2).split('.')[0]) }}.{{ maxRemovableCredits.toFixed(2).split('.')[1] }}
                            </div>
                            <div v-if="isOverLimit" class="mt-2 text-sm text-red-600 dark:text-red-400">
                                <div class="flex items-center">
                                    <svg class="h-5 w-5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                    </svg>
                                    Cannot remove more than available credits
                                </div>
                            </div>
                        </div>

                        <!-- New Balance Preview -->
                        <div class="bg-echoes-background-gray rounded-xl">
                            <div class="text-sm text-gray-500 dark:text-gray-400">New Balance After {{ creditsAction === 'add' ? 'Addition' : 'Removal' }}</div>
                            <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                                {{ formattedNewCreditsAmount }} credits
                            </div>
                        </div>
                    </div>

                    <!-- Modal Footer -->
                    <div class="px-6 py-4 bg-echoes-background-gray/50 text-right rounded-b-xl">
                        <button 
                            type="button" 
                            class="mr-3 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
                            @click="showChangeCreditsModal = false"
                        >
                            Cancel
                        </button>
                        <button 
                            v-if="creditsAction === 'add'"
                            class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/90 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-echoes-primary"
                            @click="addCredits()"
                        >
                            Confirm Addition
                        </button>
                        <button 
                            v-else
                            class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            @click="removeCredits()"
                            :disabled="isOverLimit"
                        >
                            Confirm Removal
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>



<style scoped lang="postcss">


.table-container table {
    @apply w-full table-fixed;
    display: table;
}

.table-container thead {
    @apply sticky top-0 z-10;
    background-color: var(--echoes-background-gray);
    display: table;
    width: 100%;
}

.table-container tbody {
    @apply relative;
    height: calc(100vh - 620px);
    overflow-y: auto;
    overflow-x: hidden;
    display: block;
    width: 100%;
}

.table-container tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
}



/* Custom Scrollbar */
.table-container tbody::-webkit-scrollbar {
    @apply w-2;
}

.table-container tbody::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

.table-container tbody::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

.table-container tbody::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.table-container tr {
    animation: fadeIn 0.3s ease-in-out;
}

/* Stats Cards Hover Effect */
.bg-white.dark\:bg-slate-700 {
    @apply transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style> 